/**
 * Pipeline numbers and the review lists the run screen draws from
 * `{fetchedDate}.result.json`. Counts also live on `runLog.summary`; the lists
 * do not — they stay in the stored file.
 */

import type { IntegratorRunLogSummary } from "../../../interfaces/models/IIntegratorRunLog";

export interface PipelineRow {
  found?: number;
  parsed?: number;
  rejected?: number;
  added: number;
  updated?: number;
  unchanged: number;
  deletable?: number;
  writeFailed: number;
}

export interface ImportResultSummary {
  pipeline: {
    professionals: PipelineRow;
    offices: PipelineRow;
    practiceAreas: PipelineRow;
  };
}

export interface ReviewPersonRow {
  name: string;
  email: string;
  /** Staff profile segment: Cosmos `person.id`. */
  personId?: string;
}

export interface ReviewNamedRow {
  name: string;
}

export interface ReviewIssueRow {
  item: string;
  reason: string;
}

export interface ImportReviewLists {
  deletions: ReviewPersonRow[];
  additions: ReviewPersonRow[];
  updates: ReviewPersonRow[];
  practiceAreas: ReviewNamedRow[];
  mappingIssues: ReviewIssueRow[];
  writeFailures: ReviewIssueRow[];
}

export function fromRunLogSummary(summary: IntegratorRunLogSummary): ImportResultSummary {
  return { pipeline: summary };
}

export function toImportResultSummary(raw: unknown): ImportResultSummary {
  const result = asRecord(raw);
  const mappingCounts = asRecord(result.mappingCounts);
  const personResult = asRecord(result.personResult);
  const officeResult = asRecord(result.officeResult);
  const aopResult = asRecord(result.aopResult);

  return {
    pipeline: {
      professionals: {
        found: asCount(mappingCounts.total),
        parsed: asCount(mappingCounts.mapped),
        rejected: asCount(mappingCounts.failed),
        added: countOf(personResult, "inserted"),
        updated: countOf(personResult, "updated"),
        unchanged: countOf(personResult, "existing"),
        deletable: countOf(personResult, "deletable"),
        writeFailed:
          asLength(personResult.insertFailures) + asLength(personResult.updateFailures),
      },
      offices: {
        parsed: asCount(officeResult.sourceCount),
        added: countOf(officeResult, "inserted"),
        updated: countOf(officeResult, "updated"),
        unchanged: countOf(officeResult, "existing"),
        writeFailed:
          asLength(officeResult.insertFailures) + asLength(officeResult.updateFailures),
      },
      practiceAreas: {
        parsed: asCount(aopResult.sourceCount),
        added: countOf(aopResult, "inserted"),
        unchanged: countOf(aopResult, "existing"),
        writeFailed: asLength(aopResult.insertFailures),
      },
    },
  };
}

export function toReviewLists(raw: unknown): ImportReviewLists {
  const result = asRecord(raw);
  const personResult = asRecord(result.personResult);
  const officeResult = asRecord(result.officeResult);
  const aopResult = asRecord(result.aopResult);

  return {
    deletions: toPersonRows(personResult.deletable),
    additions: toPersonRows(personResult.inserted),
    updates: toPersonRows(personResult.updated),
    practiceAreas: toPracticeAreaRows(aopResult.inserted),
    mappingIssues: toMappingIssueRows(result.mappingIssues),
    writeFailures: [
      ...toWriteFailureRows(personResult.insertFailures, "Professionals"),
      ...toWriteFailureRows(personResult.updateFailures, "Professionals"),
      ...toWriteFailureRows(officeResult.insertFailures, "Offices"),
      ...toWriteFailureRows(officeResult.updateFailures, "Offices"),
      ...toWriteFailureRows(aopResult.insertFailures, "Practice areas"),
    ],
  };
}

function toPersonRows(value: unknown): ReviewPersonRow[] {
  return asArray(value).map((entry) => {
    const person = asRecord(entry);
    const personId = personProfileId(person);
    return {
      name: personName(person),
      email: text(person.firmEmail),
      ...(personId != null ? { personId } : {}),
    };
  });
}

function personProfileId(person: Record<string, unknown>): string | undefined {
  return text(person.id) || undefined;
}

function toPracticeAreaRows(value: unknown): ReviewNamedRow[] {
  return asArray(value).map((entry) => {
    if (typeof entry === "string") {
      return { name: entry };
    }
    const area = asRecord(entry);
    return { name: text(area.displayName) || text(area.name) };
  });
}

function toMappingIssueRows(value: unknown): ReviewIssueRow[] {
  return asArray(value).map((entry) => {
    const issue = asRecord(entry);
    const parts = [
      issue.itemIndex != null ? `Item ${Number(issue.itemIndex) + 1}` : null,
      text(issue.field) || null,
    ].filter(Boolean);
    return {
      item: parts.join(" · ") || "Feed item",
      reason: text(issue.message) || text(issue.reason),
    };
  });
}

function toWriteFailureRows(value: unknown, section: string): ReviewIssueRow[] {
  return asArray(value).map((entry) => {
    const failure = asRecord(entry);
    const item = failure.item;
    const label =
      typeof item === "string"
        ? item
        : personName(asRecord(item)) || text(asRecord(item).name) || "Item";
    return {
      item: `${section}: ${label}`,
      reason: text(failure.reason),
    };
  });
}

function personName(person: Record<string, unknown>): string {
  const lastName = text(person.lastName);
  const firstName = text(person.name);
  if (lastName && firstName) {
    return `${lastName}, ${firstName}`;
  }
  return lastName || firstName || text(person.firmEmail);
}

function countOf(result: Record<string, unknown>, field: string): number {
  const named = result[`${field}Count`];
  if (typeof named === "number" && Number.isFinite(named)) {
    return named;
  }
  return asLength(result[field]);
}

function asCount(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function asLength(value: unknown): number {
  return Array.isArray(value) ? value.length : 0;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}
